import React, { useRef, useState } from "react";
import Loading from "react-fullscreen-loading";

// region Imports - External libraries
import * as Yup from "yup";
import { Button, DialogContent, DialogTitle } from "@material-ui/core";
import { TextField } from "unform-material-ui";
import { Form } from "@unform/web";
import { FormHandles } from "@unform/core";
// endregion Imports - External libraries
// region Imports - Languages
import useTranslation from "src/translations/useTranslation";
import { FilterMessages, GlobalMessages, YupMessages } from "@shared/languages/interfaces";
// endregion Imports - Languages
// region Imports - Hooks
import getValidationErrors from "@hooks/getValidationErrors";
import { useToast } from "@hooks/useToast";
// endregion Imports - Hooks
// region Imports - Services
import api from "@services/api";
// endregion Imports - Services
// region Imports - Shared
import { IFilter } from "@shared/interfaces/filter.interface";
import { Filter } from "@shared/entities/reg_filters.entity";
// endregion Imports - Shared
// region Imports - Utils
import utils, { StandardReturnObject } from "@utils/useful-functions";
// endregion Imports - Utils
// region Imports - Styles
import * as Styled from "./styles";
// endregion Imports - Styles

// region Interfaces
interface IProps {
  open: boolean;
  onClose: () => void;
  filter: IFilter;
  onSubmit: (filter: IFilter) => void;
}

interface IFormData {
  descriptionFilter: string;
}
// endregion Interfaces

const ModalFormFilterDescription: React.FC<IProps> = ({ open, onClose, filter, onSubmit }) => {
  const { t } = useTranslation();
  const { addToast } = useToast();

  const formRef = useRef<FormHandles>(null);

  const [isLoading, setIsLoading] = useState(false);

  // region Functions
  const updateFilterDescription = async (formData: IFormData): Promise<IFilter | undefined> => {

    setIsLoading(true);

    try {

      const filterUpdateDescription = {
        id_filter: filter.id_filter,
        description: formData.descriptionFilter
      };
      const response = await api.patch("/filters/update-only-description", filterUpdateDescription);
      const data = response?.data as StandardReturnObject<Filter>;

      if (data?.status === "success") {
        addToast({ type: "success", title: data.message });

        return { ...filter, description: formData.descriptionFilter };
      }

      addToast({ type: "error", title: data.message });

      return undefined;

    } catch (error) {
      utils.handleStandardError(error, t, addToast);

      return undefined;
    } finally {
      setIsLoading(false);
    }
  };

  const createFilter = async (formData: IFormData): Promise<IFilter | undefined> => {

    setIsLoading(true);

    try {

      const filterInterface = {
        ...filter,
        description: formData.descriptionFilter
      } as IFilter;

      const filterToCreate = new Filter(filterInterface);
      const response = await api.post("/filters/create", filterToCreate);
      const data = response?.data as StandardReturnObject<Filter>;

      if (data?.status === "success") {
        addToast({ type: "success", title: data.message });

        return { ...filter, description: formData.descriptionFilter, id_filter: data?.result?.id_filter };
      }

      addToast({ type: "error", title: data.message });

      return undefined;

    } catch (error) {
      utils.handleStandardError(error, t, addToast);

      return undefined;
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (formData: IFormData) => {

    try {
      const schema = Yup.object().shape({
        descriptionFilter: Yup.string()
          .required(t(YupMessages.fieldRequired))
          .max(30, t(YupMessages.fieldMaxLength30))
      });

      await schema.validate(formData, { abortEarly: false });

      let savedFilter: IFilter | undefined;

      if (filter?.id_filter) savedFilter = await updateFilterDescription(formData);
      else savedFilter = await createFilter(formData);

      if (!savedFilter) return;

      onSubmit(savedFilter);
      onClose();

    } catch (error) {
      formRef.current?.setErrors(getValidationErrors(error));
    }
  };
  // endregion Functions

  return (
    <>
      <Loading loading={isLoading} />
      <Styled.ContainerDialog
        open={open}
        onClose={onClose}
      >
        <DialogTitle>
          <div>
            {t(FilterMessages.chooseFilterName)}
          </div>
        </DialogTitle>
        <DialogContent dividers>
          <Form className="form" ref={formRef} onSubmit={handleSubmit}>
            <TextField
              label={t(FilterMessages.filterName)}
              margin="dense"
              variant="outlined"
              name="descriptionFilter"
              fullWidth
              defaultValue={filter?.description ?? ""}
            />
          </Form>
        </DialogContent>
        <Styled.DialogActions>
          <Button
            disableRipple
            onClick={() => onClose()}
            color="primary"
          >
            {t(GlobalMessages.close)}
          </Button>
          <Button
            disableRipple
            color="primary"
            onClick={() => formRef.current?.submitForm()}
          >
            {t(GlobalMessages.save)}
          </Button>
        </Styled.DialogActions>

      </Styled.ContainerDialog>
    </>

  );
};

export default ModalFormFilterDescription;
